import { useMemo } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Icon from "@mui/material/Icon";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { keyframes } from "@emotion/react";

import { get } from "lib/api";
import { firstListMediaUrl } from "lib/media";
import { useAuth } from "context/AuthContext";
import { currencyForCompany } from "lib/companyProfile";
import { isStaffRole, navigateAccountPortal } from "lib/staffFrontend";
import useStorefrontCopy from "hooks/useStorefrontCopy";
import useStorefrontHero from "storefronts/shared/useStorefrontHero";
import VbsShell from "../components/VbsShell";
import { formatMoney, resolvePrice, vbs } from "../lib/theme";

const iosUrl = String(process.env.REACT_APP_IOS_APP_URL || "").trim();
const androidUrl = String(process.env.REACT_APP_ANDROID_APP_URL || "").trim();

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to { opacity: 1; transform: translateY(0); }
`;

const softPulse = keyframes`
  0%, 100% { opacity: 0.45; transform: translateY(0); }
  50% { opacity: 1; transform: translateY(4px); }
`;

function normalizeProduct(item) {
  const attrs = item?.attributes || item || {};
  return {
    documentId: item?.documentId || attrs?.documentId,
    sku: attrs.sku,
    name: attrs.name,
    unitPrice: attrs.unitPrice,
    priceLevels: attrs.priceLevels,
    images: attrs.images,
    imageUrl: firstListMediaUrl(attrs.images),
  };
}

function ProductRailCard({ product, priceLabel, onOpen }) {
  return (
    <ButtonBase
      onClick={onOpen}
      sx={{
        flex: "0 0 auto",
        width: { xs: 196, sm: 220 },
        scrollSnapAlign: "start",
        textAlign: "left",
        display: "block",
        borderRadius: "6px",
        overflow: "hidden",
        bgcolor: vbs.surface,
        border: `1px solid ${vbs.hairline}`,
        transition: "transform 180ms ease, box-shadow 180ms ease",
        "&:hover": {
          transform: "translateY(-3px)",
          boxShadow: "0 16px 36px rgba(15,15,18,0.12)",
        },
      }}
    >
      <Box sx={{ aspectRatio: "4 / 5", bgcolor: vbs.inkSoft, overflow: "hidden", position: "relative" }}>
        {product.imageUrl ? (
          <Box
            component="img"
            src={product.imageUrl}
            alt=""
            loading="lazy"
            decoding="async"
            sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        ) : (
          <Box sx={{ width: "100%", height: "100%", display: "grid", placeItems: "center" }}>
            <Icon sx={{ color: "rgba(255,255,255,0.28)", fontSize: 36 }}>image</Icon>
          </Box>
        )}
        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: 3,
            background: vbs.swoosh,
            opacity: 0.9,
          }}
        />
      </Box>
      <Box sx={{ p: 1.5 }}>
        <Typography
          sx={{
            fontFamily: vbs.fontMono,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: 0.7,
            color: vbs.accent,
            textTransform: "uppercase",
          }}
        >
          {product.sku || "SKU"}
        </Typography>
        <Typography
          sx={{
            mt: 0.4,
            fontSize: 14.5,
            fontWeight: 700,
            lineHeight: 1.25,
            color: vbs.ink,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            minHeight: "2.5em",
          }}
        >
          {product.name}
        </Typography>
        <Typography sx={{ mt: 0.75, fontSize: 15, fontWeight: 800, color: vbs.ink }}>
          {priceLabel}
        </Typography>
      </Box>
    </ButtonBase>
  );
}


ProductRailCard.propTypes = {
  product: PropTypes.shape({
    documentId: PropTypes.string,
    sku: PropTypes.string,
    name: PropTypes.string,
    imageUrl: PropTypes.string,
  }).isRequired,
  priceLabel: PropTypes.string.isRequired,
  onOpen: PropTypes.func.isRequired,
};

export default function HomePage() {
  const navigate = useNavigate();
  const { token, pluginRole, storefrontTenant, activeCompany } = useAuth();
  const { t } = useStorefrontCopy();
  const staffLoggedIn = !!token && isStaffRole(pluginRole);
  const isTablet = useMediaQuery(`(min-width:${vbs.tabletBp}px)`);
  const companyKey = String(storefrontTenant || activeCompany || "vbsus").toLowerCase();
  const { imageUrl: heroImage } = useStorefrontHero(companyKey);

  const { data, isLoading } = useQuery({
    queryKey: ["vbsus-mobile-home-products", companyKey, isTablet ? "tablet" : "phone"],
    queryFn: () =>
      get("/api/storefront/catalog", {
        sort: "updatedAt:desc",
        pagination: { pageSize: isTablet ? 10 : 8 },
      }),
    staleTime: 30000,
  });

  const currency = data?.meta?.company?.baseCurrency || currencyForCompany(companyKey);
  const products = useMemo(() => (data?.data || []).map(normalizeProduct), [data]);

  const trust = [
    {
      stat: t("home.trustStatQc", "QC"),
      label: t("home.trustQc", "Calibration documented on every unit"),
    },
    {
      stat: t("home.trustStatCore", "CORE"),
      label: t("home.trustCore", "Tracked core returns, no guesswork"),
    },
    {
      stat: t("home.trustStatWarranty", "Warranty"),
      label: t("home.trustWarranty", "Register and claim from your portal"),
    },
  ];
  if (isTablet) {
    trust.push({
      stat: t("home.trustStatSupport", "Support"),
      label: t("home.trustSupport", "Shop-direct technical help"),
    });
  }

  const openDownload = (url) => {
    if (url) {
      window.location.href = url;
      return;
    }
    navigate("/m/qr");
  };

  return (
    <VbsShell showLogo>
      <Box sx={{ bgcolor: vbs.canvas, color: vbs.ink }}>
        {/* ——— Cinematic first viewport ——— */}
        <Box
          sx={{
            position: "relative",
            minHeight: {
              xs: "calc(100dvh - 160px)",
              sm: "min(72vh, 680px)",
            },
            display: "flex",
            flexDirection: "column",
            justifyContent: "stretch",
            overflow: "hidden",
            bgcolor: vbs.ink,
          }}
        >
          {heroImage ? (
            <Box
              component="img"
              src={heroImage}
              alt=""
              sx={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: isTablet ? "center 30%" : "center 28%",
                transform: "scale(1.04)",
                animation: `${fadeUp} 900ms ease both`,
              }}
            />
          ) : (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background: `radial-gradient(ellipse at 30% 20%, ${vbs.inkSoft} 0%, ${vbs.ink} 55%)`,
              }}
            />
          )}

          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: isTablet
                ? "linear-gradient(105deg, rgba(15,15,18,0.92) 0%, rgba(15,15,18,0.55) 42%, rgba(15,15,18,0.35) 100%)"
                : "linear-gradient(180deg, rgba(15,15,18,0.35) 0%, rgba(15,15,18,0.2) 38%, rgba(15,15,18,0.88) 100%)",
            }}
          />

          <Box
            sx={{
              position: "relative",
              zIndex: 2,
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
              px: { xs: 2.25, sm: 4 },
              pt: { xs: 3, sm: 4 },
              pb: { xs: 3.5, sm: 4.5 },
              maxWidth: isTablet ? 560 : vbs.tabletMax,
              width: "100%",
              ...(isTablet ? { ml: { sm: 2 } } : { mx: "auto" }),
            }}
          >
            <Box sx={{ animation: `${fadeUp} 700ms ease both` }}>
            <Typography
              component="h1"
              sx={{
                mt: 0,
                fontSize: { xs: "2.05rem", sm: "2.75rem" },
                fontWeight: 800,
                lineHeight: 1.05,
                letterSpacing: "-0.035em",
                color: "#fff",
                maxWidth: 440,
                textShadow: "0 10px 40px rgba(0,0,0,0.35)",
                animation: `${fadeUp} 800ms ease 120ms both`,
              }}
            >
              {t("home.heroTitle", "Clean shifts start with controlled hydraulics.")}
            </Typography>

            <Typography
              sx={{
                mt: 1.5,
                fontSize: { xs: 15, sm: 16.5 },
                lineHeight: 1.55,
                color: "rgba(255,255,255,0.78)",
                maxWidth: 420,
                animation: `${fadeUp} 850ms ease 180ms both`,
              }}
            >
              {t(
                "home.heroSubtitle",
                "Premium remanufactured valve bodies — measured, calibrated, and ready for the bay.",
              )}
            </Typography>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.25}
              sx={{ mt: 3, animation: `${fadeUp} 900ms ease 240ms both` }}
            >
              <ButtonBase
                onClick={() => navigate("/m/catalog")}
                sx={{
                  bgcolor: vbs.accent,
                  color: "#fff",
                  px: 2.5,
                  py: 1.55,
                  borderRadius: "6px",
                  fontSize: 15,
                  fontWeight: 800,
                  letterSpacing: 0.02,
                  minWidth: { sm: 200 },
                  justifyContent: "space-between",
                  gap: 1.5,
                  boxShadow: "0 12px 28px rgba(255,92,0,0.38)",
                  "&:hover": { bgcolor: vbs.accentHover },
                }}
              >
                {t("home.primaryCta", "Shop valve bodies")}
                <Icon sx={{ fontSize: 22 }}>arrow_forward</Icon>
              </ButtonBase>

            </Stack>
            </Box>
          </Box>

          <Box
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 10,
              display: "flex",
              justifyContent: "center",
              zIndex: 2,
              animation: `${softPulse} 2.2s ease-in-out infinite`,
            }}
          >
            <Icon sx={{ color: "rgba(255,255,255,0.55)", fontSize: 26 }}>keyboard_arrow_down</Icon>
          </Box>
        </Box>

        {/* ——— Featured rail ——— */}
        <Box sx={{ pt: { xs: 3.5, sm: 4.5 }, pb: { xs: 2, sm: 2.5 } }}>
          <Box
            sx={{
              px: { xs: 2.25, sm: 3 },
              maxWidth: vbs.tabletMax,
              mx: "auto",
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "space-between",
              gap: 2,
              mb: 2,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: 11.5,
                  fontWeight: 800,
                  letterSpacing: 1.2,
                  textTransform: "uppercase",
                  color: vbs.accent,
                }}
              >
                {t("home.featuredEyebrow", "In stock & ready")}
              </Typography>
              <Typography
                sx={{
                  mt: 0.4,
                  fontSize: { xs: 24, sm: 28 },
                  fontWeight: 800,
                  letterSpacing: -0.4,
                  color: vbs.ink,
                }}
              >
                {t("home.featuredTitle", "Shop valve bodies")}
              </Typography>
            </Box>
            <ButtonBase
              onClick={() => navigate("/m/catalog")}
              sx={{ display: "flex", alignItems: "center", gap: 0.35, pb: 0.5, flexShrink: 0 }}
            >
              <Typography sx={{ fontSize: 14.5, fontWeight: 750, color: vbs.accent }}>
                {t("home.featuredLink", "Full catalog")}
              </Typography>
              <Icon sx={{ fontSize: 20, color: vbs.accent }}>arrow_forward</Icon>
            </ButtonBase>
          </Box>

          {isLoading ? (
            <Typography sx={{ px: 2.25, color: vbs.muted }}>
              {t("home.loadingProducts", "Loading products…")}
            </Typography>
          ) : products.length ? (
            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                overflowX: "auto",
                scrollSnapType: "x mandatory",
                px: { xs: 2.25, sm: 3 },
                pb: 1,
                maxWidth: vbs.tabletMax,
                mx: "auto",
                "&::-webkit-scrollbar": { display: "none" },
                scrollbarWidth: "none",
              }}
            >
              {products.map((product) => (
                <ProductRailCard
                  key={product.documentId}
                  product={product}
                  priceLabel={formatMoney(
                    resolvePrice(product),
                    currency,
                    t("catalog.priceQuote", "Request quote"),
                  )}
                  onOpen={() => navigate(`/m/catalog/${product.documentId}`)}
                />
              ))}
            </Box>
          ) : (
            <Typography sx={{ px: 2.25, color: vbs.muted }}>
              {t("catalog.empty", "No products are available right now.")}
            </Typography>
          )}
        </Box>

        {/* ——— Trust strip (typography, not cards) ——— */}
        <Box
          sx={{
            borderTop: `1px solid ${vbs.hairline}`,
            borderBottom: `1px solid ${vbs.hairline}`,
            bgcolor: vbs.surface,
            py: { xs: 2.75, sm: 3.25 },
          }}
        >
          <Box
            sx={{
              maxWidth: vbs.tabletMax,
              mx: "auto",
              px: { xs: 2.25, sm: 3 },
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: `repeat(${trust.length}, 1fr)`,
              },
              gap: { xs: 2, sm: 2.5 },
            }}
          >
            {trust.map((item) => (
              <Box key={item.stat} sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                <Box
                  sx={{
                    mt: 0.35,
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: vbs.swoosh,
                    flexShrink: 0,
                  }}
                />
                <Box>
                  <Typography
                    sx={{
                      fontFamily: vbs.fontMono,
                      fontSize: 12,
                      fontWeight: 800,
                      letterSpacing: 0.8,
                      color: vbs.accent,
                    }}
                  >
                    {item.stat}
                  </Typography>
                  <Typography sx={{ mt: 0.35, fontSize: 14, lineHeight: 1.45, color: vbs.muted }}>
                    {item.label}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* ——— App ——— */}
        <Box sx={{ maxWidth: vbs.tabletMax, mx: "auto", px: { xs: 2.25, sm: 3 }, py: { xs: 3, sm: 3.5 } }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ sm: "center" }}
            justifyContent="space-between"
          >
            <Box>
              <Typography sx={{ fontSize: 17, fontWeight: 800, color: vbs.ink }}>
                {t("home.appTitle", "Take the shop with you")}
              </Typography>
              <Typography sx={{ mt: 0.4, fontSize: 14, color: vbs.muted, lineHeight: 1.45 }}>
                {t("home.appBody", "Catalog, orders, and account tools in the VBS app.")}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} sx={{ width: { xs: "100%", sm: "auto" } }}>
              {[
                { label: "iOS", icon: "phone_iphone", url: iosUrl },
                { label: "Android", icon: "android", url: androidUrl },
              ].map((row) => (
                <ButtonBase
                  key={row.label}
                  onClick={() => openDownload(row.url)}
                  sx={{
                    flex: 1,
                    minWidth: { sm: 120 },
                    py: 1.15,
                    px: 1.5,
                    borderRadius: "6px",
                    border: `1px solid ${vbs.hairline}`,
                    bgcolor: vbs.surface,
                    fontWeight: 750,
                    fontSize: 13.5,
                    color: vbs.ink,
                    gap: 0.75,
                    "&:hover": { borderColor: "rgba(255,92,0,0.38)", bgcolor: vbs.accentSoft },
                  }}
                >
                  <Icon sx={{ fontSize: 18 }}>{row.icon}</Icon>
                  {row.label}
                </ButtonBase>
              ))}
            </Stack>
          </Stack>
        </Box>

        {/* ——— Closing band ——— */}
        <Box
          sx={{
            bgcolor: vbs.ink,
            color: "#fff",
            py: { xs: 4.5, sm: 5.5 },
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              top: 0,
              height: 4,
              background: vbs.swoosh,
            }}
          />
          <Box
            sx={{
              maxWidth: vbs.tabletMax,
              mx: "auto",
              px: { xs: 2.25, sm: 3 },
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { sm: "center" },
              justifyContent: "space-between",
              gap: 2.5,
            }}
          >
            <Box sx={{ maxWidth: 460 }}>
              <Typography
                sx={{
                  fontSize: { xs: 22, sm: 26 },
                  fontWeight: 800,
                  letterSpacing: -0.35,
                  lineHeight: 1.15,
                }}
              >
                {t("home.ctaBandTitle", "Built for transmission shops")}
              </Typography>
              <Typography sx={{ mt: 1, fontSize: 14.5, lineHeight: 1.55, color: "rgba(255,255,255,0.72)" }}>
                {t(
                  "home.ctaBandBody",
                  "Order parts, register units, manage cores, and open warranty claims — all from one account.",
                )}
              </Typography>
            </Box>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ minWidth: { sm: 260 } }}>
              <ButtonBase
                onClick={() =>
                  navigateAccountPortal({ token, role: pluginRole, navigate })
                }
                sx={{
                  bgcolor: vbs.accent,
                  color: "#fff",
                  px: 2.25,
                  py: 1.4,
                  borderRadius: "6px",
                  fontWeight: 800,
                  fontSize: 14,
                  "&:hover": { bgcolor: vbs.accentHover },
                }}
              >
                {staffLoggedIn
                  ? t("home.openStaffErp", "Open Staff ERP")
                  : token
                    ? t("home.goToPortal", "Go to portal")
                    : t("home.secondaryCta", "Sign in")}
              </ButtonBase>
              {!token ? (
                <ButtonBase
                  onClick={() => navigate("/m/register")}
                  sx={{
                    color: "#fff",
                    px: 2.25,
                    py: 1.3,
                    borderRadius: "6px",
                    border: "1px solid rgba(255,255,255,0.35)",
                    fontWeight: 750,
                    fontSize: 14,
                  }}
                >
                  {t("home.createAccount", "Create account")}
                </ButtonBase>
              ) : null}
            </Stack>
          </Box>
        </Box>
      </Box>
    </VbsShell>
  );
}
